import { useState, useEffect } from "react";
import { Plus, Trash2, GripVertical, ImageIcon, ExternalLink } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";

import { API } from "../../config/api";

export default function AdminAboutImages() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    url: "",
    order_index: 0
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchImages();
  }, []);

  const getToken = () => localStorage.getItem("admin_token");

  const fetchImages = async () => {
    try {
      const response = await fetch(`${API}/admin/about-images`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.ok) {
        setImages(await response.json());
      }
    } catch (error) {
      toast.error("Failed to load images");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      const response = await fetch(`${API}/admin/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: uploadData
      });

      if (response.ok) {
        const data = await response.json();
        const isProduction = window.location.hostname === 'tatvamasi.ca';
        const API_BASE = isProduction ? "" : (process.env.REACT_APP_BACKEND_URL || "");
        const fullUrl = `${API_BASE}${data.url}`;
        setFormData({ ...formData, url: fullUrl });
        toast.success("Image uploaded!");
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.url) {
      toast.error("Please upload an image or provide a URL");
      return;
    }

    try {
      const response = await fetch(`${API}/admin/about-images`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          ...formData,
          order_index: images.length
        })
      });

      if (response.ok) {
        toast.success("Image added!");
        setDialogOpen(false);
        setFormData({ title: "", url: "", order_index: 0 });
        fetchImages();
      } else {
        toast.error("Failed to add image");
      }
    } catch {
      toast.error("Failed to add image");
    }
  };

  const handleDelete = async (imageId) => {
    if (!confirm("Are you sure you want to delete this image?")) return;

    try {
      const response = await fetch(`${API}/admin/about-images/${imageId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      if (response.ok) {
        toast.success("Image deleted");
        fetchImages();
      } else {
        toast.error("Failed to delete image");
      }
    } catch {
      toast.error("Failed to delete image");
    }
  };

  const updateOrder = async (imageId, newIndex) => {
    try {
      await fetch(`${API}/admin/about-images/${imageId}?order_index=${newIndex}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      fetchImages();
    } catch {
      toast.error("Failed to update order");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-about-images">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-heading text-2xl text-kerala-green">About Page Images</h1>
          <p className="text-gray-600">Manage photos displayed on the About Us page</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-about-image-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Image
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Image</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title / Caption</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Community gathering..."
                  required
                  data-testid="about-image-title-input"
                />
              </div>

              <div>
                <Label>Image</Label>
                <div className="mt-2 space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="flex-1">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-kerala-green transition-colors">
                        <ImageIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          {uploading ? "Uploading..." : "Click to upload image"}
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  </div>
                  
                  <div className="text-center text-sm text-gray-500">or</div>
                  
                  <Input
                    placeholder="Paste image URL"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    data-testid="about-image-url-input"
                  />
                </div>

                {formData.url && (
                  <div className="mt-3">
                    <img
                      src={formData.url}
                      alt="Preview"
                      className="w-full h-40 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>

              <Button type="submit" className="btn-primary w-full" data-testid="submit-about-image-btn">
                Add Image
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-lg">
          <ImageIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-600">No images yet</p>
          <p className="text-gray-400 text-sm">Add images to display on the About Us page</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="bg-white rounded-lg shadow overflow-hidden group"
              data-testid={`about-image-${image.id}`}
            >
              <div className="aspect-square relative">
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <a
                    href={image.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white rounded-full mr-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="p-2 bg-red-500 text-white rounded-full"
                    data-testid={`delete-about-image-${image.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">#{index + 1}</span>
                </div>
                <p className="font-medium text-kerala-green truncate">{image.title}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
